import { formatCPF } from "@brazilian-utils/brazilian-utils";
import {
  Box, 
  Divider,
  Flex,
  Grid,
  Text,
  Link,
} from "@chakra-ui/layout";
import { Heading, Image } from "@chakra-ui/react";
import { Row, Col } from "antd";
import useConfirmarAnuncio from "features/anuncio/hooks/useConfirmarAnuncio";
import { useDetalheAnuncio } from "features/anuncio/hooks/useDetalheAnuncio";
import useRejeitarAnuncio from "features/anuncio/hooks/useRejeitarAnuncio";
import { AnuncioStatusEnum } from "features/anuncio/typings";
import { useBreadcrumb } from "features/breadcrumbs/index";
import colors from "core/resources/theme/colors";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { formatMoney, formatPhone } from "infra/helpers/number";
import { useState } from "react";
import { useParams } from "react-router-dom";
import PrimaryButton from "components/Button/PrimaryButton";
import CardPerson from "components/Cards/CardPerson";
import { useLoading } from "components/GlobalLoading";
import PageHeader from "components/Page/PageHeader/index";
import Historico from "./components/Historico";
import ModalRejeitarMotivo from "components/ModalRejeitarMotivo/ModalRejeitarMotivo";
import { PermissaoEnum } from "core/resources/enums/permissao";
import Container from "components/Container";

function AnaliseAnuncio() {
  const { id } = useParams() as {
    id: string;
  };
  const { data, isLoading, isFetching, refetch } = useDetalheAnuncio(id);
  const { mutate: confirmarAnuncio, isLoading: isLoadingConfirmar } =
    useConfirmarAnuncio();
  const { mutate: rejeitarAnuncio, isLoading: isLoadingRejeitar } =
    useRejeitarAnuncio();

  const [rejeitarModalOpen, setRejeitarModalOpen] = useState(false);

  useBreadcrumb([
    { name: "Anuncios", url: "/administracao/anuncios" },
    {
      name: data?.nome || "Carregando...",
    },
  ]);

  useLoading(isLoading);

  const _isLoading = isLoading || isFetching;

  const _isLoadingSave = isLoadingConfirmar || isLoadingRejeitar;
  return (
    <>
      <Container justifyContent="space-between">
        <Flex maxW="container.xl" justifyContent="space-between">
          <PageHeader title="Gestão de Anúncios" />
          {!_isLoading && data ? (
            <Flex gridGap={[4, 4]}>
              {data?.status.id !== AnuncioStatusEnum.Rejeitado ? (
                <PrimaryButton
                  bgColor={colors.red}
                  permissions={[
                    PermissaoEnum.Backoffice.Administrativo.Anuncio.Rejeitar,
                  ]}
                  disabled={_isLoadingSave}
                  isLoading={_isLoadingSave}
                  onClick={() => setRejeitarModalOpen(true)}
                >
                  Rejeitar
                </PrimaryButton>
              ) : null}
              {data?.status.id !== AnuncioStatusEnum.Aprovado ? (
                <PrimaryButton
                  bgColor={colors.green500}
                  disabled={_isLoadingSave}
                  permissions={[
                    PermissaoEnum.Backoffice.Administrativo.Anuncio.Aprovar,
                  ]}
                  isLoading={_isLoadingSave}
                  onClick={() => {
                    confirmarAnuncio(data?.id!, {
                      onError: (e) => showErrorAxios(e),
                      onSuccess: () => {
                        showSuccess("Anuncio aprovado com sucesso.").then(() =>
                          refetch()
                        );
                      },
                    });
                  }}
                >
                  Aprovar
                </PrimaryButton>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
        <Box as={Row} gutter={[24, 24]}>
          <Flex
            as={Col}
            boxShadow="0 0 24px rgba(0,0,0,.05)"
            bg="white"
            rounded="16px"
            p="24px"
            md={12}
            xs={24}
            height="max-content"
          >
            <Box>
              <Flex>
                <Heading mb={4} size="md">
                  Detalhes do anuncio
                </Heading>
              </Flex>

              <Text mb={2} fontWeight="bold" fontSize={20}>
                {data?.nome}
              </Text>
              <Text fontWeight="bold">{formatMoney(data?.valor)}</Text>
              <Divider m={2} />

              <Flex>
                <Heading mb={4} size="10px">
                  Descrição
                </Heading>
              </Flex>

              <Text>{data?.descricao}</Text>
              <Divider m={2} />

              <Flex>
                <Heading mb={4} size="10px">
                  Imagens
                </Heading>
              </Flex>
              <Grid templateColumns="repeat(2, 1fr)" gap="24px">
                {data?.imagens.map((imagem) => (
                  <Link
                    href={imagem.referencia}
                    target="_blank"
                    rel="noopener noreferrer"
                    rounded="lg"
                  >
                    <Image
                      src={imagem.referencia}
                      width="100%"
                      height="175px"
                      fit="cover"
                      rounded="lg"
                    />
                  </Link>
                ))}
              </Grid>
            </Box>
          </Flex>
          <Flex
            flexDirection="column"
            as={Col}
            md={12}
            xs={24}
            gridGap={"24px"}
          >
            <Flex
              boxShadow="0 0 24px rgba(0,0,0,.05)"
              flexDirection="column"
              bg="white"
              rounded="16px"
              p="24px"
              height="max-content"
            >
              <Flex>
                <Heading mb={4} size="md">
                  Anunciante
                </Heading>
              </Flex>
              <Box py="4px">
                <Flex justifyContent="space-between" flexDirection="column">
                  <CardPerson
                    avatarUrl={data?.pessoa.imagemUrl || ""}
                    nome={data?.pessoa.nome || ""}
                    avatarProps={{ opacity: 1 }}
                  />
                  <Flex justifyContent="space-between" mt={8} mb={8}>
                    <Flex direction="column">
                      <Flex color="#768398" fontSize="12px">
                        TELEFONE:
                      </Flex>
                      <Flex fontSize="12px">
                        {formatPhone(data?.pessoa.telefone || "")}
                      </Flex>
                    </Flex>

                    <Flex direction="column">
                      <Flex color="#768398" fontSize="12px">
                        CPF:
                      </Flex>
                      <Flex fontSize="12px">
                        {formatCPF(data?.pessoa.cpf || "")}
                      </Flex>
                    </Flex>

                    <Flex direction="column">
                      <Flex color="#768398" fontSize="12px">
                        E-MAIL:
                      </Flex>
                      <Flex fontSize="12px">{data?.pessoa.email}</Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            </Flex>
            <Flex
              boxShadow="0 0 24px rgba(0,0,0,.05)"
              flexDirection="column"
              bg="white"
              rounded="16px"
              p="24px"
              height="max-content"
            >
              <Historico logs={data?.logs || []} />
            </Flex>
          </Flex>
        </Box>
      </Container>

      <ModalRejeitarMotivo
        isLoadingSave={_isLoadingSave}
        visible={rejeitarModalOpen}
        onClose={() => setRejeitarModalOpen(false)}
        onSubmit={(motivo) => {
          rejeitarAnuncio(
            {
              id,
              motivo,
            },
            {
              onError: (e) => showErrorAxios(e),
              onSuccess: () =>
                showSuccess("Anuncio rejeitado com sucesso").then(() => {
                  setRejeitarModalOpen(false);
                  refetch();
                }),
            }
          );
        }}
      />
    </>
  );
}

export default AnaliseAnuncio;
